import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Zap, TrendingUp, Clock, BarChart3, MessageSquare, CheckCircle, ChevronDown } from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const staggerContainer = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };

export default function HomePage() {
  const [expandedFaq, setExpandedFaq] = React.useState(0);

  // FAQ Schema JSON-LD
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does setup take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Happy is built for speed. You'll be fully set up in 1-2 days. We handle all the technical work — you just connect your calendar and social accounts."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use Happy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Happy is designed for restaurant owners, not developers. Everything is point-and-click. If you can use Instagram, you can use Happy."
        }
      },
      {
        "@type": "Question",
        "name": "What if I already use another booking system?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Happy integrates with Resy, OpenTable, Toast, and most POS systems. We can migrate your existing bookings automatically."
        }
      },
      {
        "@type": "Question",
        "name": "Can Happy handle my restaurant's unique needs?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Our AI learns from your restaurant's data — your hours, your cuisine type, your customer patterns. The longer you use Happy, the smarter it gets."
        }
      },
      {
        "@type": "Question",
        "name": "What's your cancellation policy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Cancel anytime, no questions asked. No long-term contracts, no setup fees, no hidden charges. We only win if you love Happy."
        }
      }
    ]
  };

  const coreFeatures = [
    {
      icon: Clock,
      title: 'Booking Automation',
      subtitle: 'Never miss a lead',
      desc: 'AI-powered booking system that captures, confirms, and manages all customer bookings 24/7. Automated reminders reduce no-shows by 40%.',
    },
    {
      icon: TrendingUp,
      title: 'Social Media Management',
      subtitle: 'Post daily, auto-optimized',
      desc: 'AI writes and schedules daily posts optimized for your audience. Consistent presence without the manual work.',
    },
    {
      icon: BarChart3,
      title: 'Inventory Intelligence',
      subtitle: 'Stock alerts, cost tracking',
      desc: 'Real-time inventory tracking with automated reorder alerts. Know your food costs and margins instantly.',
    },
  ];

  const testimonials = [
    {
      name: 'Maria Santos',
      restaurant: "Maria's Pizzeria, Austin TX",
      quote: 'Happy automated our bookings and we went from 20 to 60 reservations per week. The AI even reminds customers the day before.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
    },
    {
      name: 'James Chen',
      restaurant: 'Chen Kitchen, Dallas TX',
      quote: 'We were spending 10 hours a week on marketing. Happy handles it all. Our social media engagement tripled in 2 months.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    },
    {
      name: 'Sofia Rodriguez',
      restaurant: 'Casa Sofia, Houston TX',
      quote: 'The inventory tracking alone saved us $2K per month in waste. Happy pays for itself in the first week.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop',
    },
  ];

  const pricingTiers = [
    {
      name: 'Starter',
      price: '$99',
      period: '/month',
      desc: 'Perfect for new restaurants',
      features: [
        'Basic booking automation',
        'AI social media (3 posts/week)',
        'Inventory tracking',
        'Email support',
        'Up to 500 bookings/month',
      ],
      cta: 'Get Started',
      primary: false,
    },
    {
      name: 'Professional',
      price: '$299',
      period: '/month',
      desc: 'Most popular choice',
      features: [
        'Advanced booking + reminders',
        'AI social media (daily posts)',
        'Full inventory + cost analysis',
        'Lead capture & CRM',
        'Priority support',
        'Unlimited bookings',
      ],
      cta: 'Start Free Trial',
      primary: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'pricing',
      desc: 'For multi-location chains',
      features: [
        'Everything in Professional',
        'Multi-location dashboard',
        'Custom integrations',
        'Dedicated account manager',
        'Advanced analytics',
        'Custom AI training',
      ],
      cta: 'Contact Sales',
      primary: false,
    },
  ];

  const faqs = [
    {
      q: 'How long does setup take?',
      a: 'Happy is built for speed. You\'ll be fully set up in 1-2 days. We handle all the technical work — you just connect your calendar and social accounts.',
    },
    {
      q: 'Do I need technical skills to use Happy?',
      a: 'No. Happy is designed for restaurant owners, not developers. Everything is point-and-click. If you can use Instagram, you can use Happy.',
    },
    {
      q: 'What if I already use another booking system?',
      a: 'Happy integrates with Resy, OpenTable, Toast, and most POS systems. We can migrate your existing bookings automatically.',
    },
    {
      q: 'Can Happy handle my restaurant\'s unique needs?',
      a: 'Yes. Our AI learns from your restaurant\'s data — your hours, your cuisine type, your customer patterns. The longer you use Happy, the smarter it gets.',
    },
    {
      q: 'What\'s your cancellation policy?',
      a: 'Cancel anytime, no questions asked. No long-term contracts, no setup fees, no hidden charges. We only win if you love Happy.',
    },
  ];

  return (
    <div className="bg-white">
      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0 -z-10">
          <img
            src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-22/d215d33e788d.png"
            alt="SaaS dashboard background"
            className="w-full h-full object-cover"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 via-blue-800/60 to-blue-900/70" />
        </div>

        <div className="max-w-4xl mx-auto text-center z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="inline-block bg-blue-400/20 text-blue-200 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6 border border-blue-300/40">
              🚀 The AI Marketing Execution Platform
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
          >
            Restaurant Marketing Automation: AI That <span className="text-blue-300">Executes</span> For You
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl sm:text-2xl text-blue-100 leading-relaxed mb-8 max-w-2xl mx-auto"
          >
            1-2 day setup. 24/7 automation. No setup fee.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <a
              href="#demo"
              className="inline-flex items-center justify-center gap-2 bg-blue-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-400 transition-all shadow-lg shadow-blue-500/50 hover:shadow-blue-400/60"
            >
              Book Demo <ArrowRight className="w-5 h-5" />
            </a>
            <button
              className="inline-flex items-center justify-center gap-2 border-2 border-blue-300 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-400/20 transition-colors"
            >
              Watch Demo
            </button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-6 justify-center text-sm text-blue-100"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>No setup fee</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>1-2 day setup</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 1: PROBLEM/PAIN */}
      <section id="problem" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Restaurant Marketing Challenges: Why Manual Booking & Marketing Waste Your Time
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              You're managing bookings manually, posting sporadically on social media, and guessing at what works. 
              Meanwhile, your competitors are automating everything and capturing your customers. It's not a fair fight.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12"
          >
            {[
              { icon: '⏱️', title: 'Time Drain', desc: 'Booking calls, email follow-ups, manual scheduling' },
              { icon: '📱', title: 'Social Media Chaos', desc: 'Inconsistent posting, low engagement, unclear ROI' },
              { icon: '💰', title: 'Lost Revenue', desc: 'Missed bookings, no-shows, inventory waste' },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-white p-8 rounded-xl border border-gray-200 text-center"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: SOLUTION */}
      <section id="solution" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block bg-blue-100 text-blue-600 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              The Solution
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Restaurant Automation Solution: Happy POS Executes Your Marketing 24/7
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Stop managing marketing. Start automating it. Happy handles bookings, social media, inventory, 
              and customer engagement — 24/7, with zero manual work.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12"
          >
            {[
              { icon: '✅', title: 'Fully Automated', desc: 'Bookings, reminders, social posts — all on autopilot' },
              { icon: '🧠', title: 'AI-Powered', desc: 'Learns your restaurant, optimizes for your customers' },
              { icon: '📊', title: 'Data-Driven', desc: 'Real-time analytics on bookings, revenue, and ROI' },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 3: CORE FEATURES (3-COLUMN GRID) */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block bg-blue-100 text-blue-600 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              Core Features
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">Built for restaurant growth</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {coreFeatures.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  variants={fadeUp}
                  className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all group"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                    <Icon className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-blue-600 font-semibold mb-4">{feature.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* SECTION 4: SOCIAL PROOF (TESTIMONIALS) */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block bg-blue-100 text-blue-600 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              Social Proof
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">Loved by restaurant owners</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-all"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 font-medium mb-6 leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center gap-4">
                  <img src={t.image} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-bold text-gray-900">{t.name}</p>
                    <p className="text-sm text-gray-600">{t.restaurant}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 5: PRICING */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block bg-blue-100 text-blue-600 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              Pricing
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Simple, transparent pricing</h2>
            <p className="text-xl text-gray-600">No setup fees. No long-term contracts. Cancel anytime.</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {pricingTiers.map((tier, i) => (
              <motion.div
                key={tier.name}
                variants={fadeUp}
                className={`rounded-2xl p-8 transition-all ${
                  tier.primary
                    ? 'bg-blue-600 text-white border-2 border-blue-600 shadow-xl scale-105'
                    : 'bg-white border-2 border-gray-200 hover:border-blue-200'
                }`}
              >
                <h3 className={`text-2xl font-bold mb-2 ${tier.primary ? 'text-white' : 'text-gray-900'}`}>
                  {tier.name}
                </h3>
                <p className={`text-sm mb-6 ${tier.primary ? 'text-blue-100' : 'text-gray-600'}`}>
                  {tier.desc}
                </p>
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${tier.primary ? 'text-white' : 'text-gray-900'}`}>
                    {tier.price}
                  </span>
                  <span className={tier.primary ? 'text-blue-100' : 'text-gray-600'}>{tier.period}</span>
                </div>

                <button
                  className={`w-full py-3 rounded-lg font-bold mb-8 transition-all ${
                    tier.primary
                      ? 'bg-white text-blue-600 hover:bg-gray-100'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {tier.cta}
                </button>

                <ul className="space-y-4">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        tier.primary ? 'text-blue-100' : 'text-green-600'
                      }`} />
                      <span className={tier.primary ? 'text-blue-50' : 'text-gray-700'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 6: FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block bg-blue-100 text-blue-600 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              FAQ
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">Common questions</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4"
          >
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? -1 : i)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-bold text-gray-900 text-left">{faq.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-600 flex-shrink-0 transition-transform ${
                      expandedFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="px-6 py-4 bg-gray-50 border-t border-gray-200"
                  >
                    <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 7: FINAL CTA */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to automate your marketing?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join 100+ restaurants already using Happy. See a live demo in 15 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors">
                Book Demo <ArrowRight className="w-5 h-5" />
              </button>
              <button className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition-all">
                Schedule Call
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 8: FOOTER */}
      <footer className="bg-gray-900 text-gray-300 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <img
                src="https://storage.googleapis.com/happyspace-preview-projects/uploads/general/7437022f0a2500338818d317a8952de8af5f38fe547572c1a4b6f77eca63403c.png"
                alt="Happy"
                className="h-8 w-auto mb-4"
              />
              <p className="text-sm leading-relaxed">
                AI marketing automation platform for restaurants. 24/7 booking, social media, and inventory management.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#demo" className="hover:text-white transition-colors">Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center text-sm">
            <p>&copy; 2026 Happy. All rights reserved.</p>
            <div className="flex gap-6 mt-4 sm:mt-0">
              <a href="#" className="hover:text-white transition-colors">Twitter</a>
              <a href="#" className="hover:text-white transition-colors">LinkedIn</a>
              <a href="#" className="hover:text-white transition-colors">Instagram</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
